import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Card,
  CardContent,
  TextField,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Tooltip,
} from '@mui/material';
import { Visibility as VisibilityIcon } from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { useParams } from 'react-router-dom';
import AccountLayout from '../../layouts/AccountLayout';
import { fetchWithAuth } from '../../utils/apiUtils';

function AIUsageDashboard() {
  const { accountId } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [usageData, setUsageData] = useState(null);
  const [dateRange, setDateRange] = useState({
    startDate: new Date(new Date().setDate(new Date().getDate() - 30)), // Last 30 days
    endDate: new Date(),
  });
  const [previewDialog, setPreviewDialog] = useState({
    open: false,
    content: '',
    prompt: '',
  });

  const fetchUsageData = async () => {
    setLoading(true);
    try {
      const queryParams = new URLSearchParams({
        accountId,
        startDate: dateRange.startDate.toISOString(),
        endDate: dateRange.endDate.toISOString(),
      });

      const data = await fetchWithAuth(`/api/ai/usage/stats?${queryParams}`);
      setUsageData(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsageData();
  }, [accountId, dateRange]);

  const handlePreviewContent = (log) => {
    setPreviewDialog({
      open: true,
      content: log.response?.text || 'No content available',
      prompt: log.prompt || 'No prompt available'
    });
  };

  return (
    <AccountLayout>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" gutterBottom>
          AI Usage Dashboard
        </Typography>

        <Paper sx={{ p: 2, mb: 3 }}>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
              <DatePicker
                label="Start Date"
                value={dateRange.startDate}
                onChange={(newValue) => {
                  setDateRange(prev => ({ ...prev, startDate: newValue }));
                }}
                renderInput={(params) => <TextField {...params} />}
                maxDate={dateRange.endDate}
              />
              <DatePicker
                label="End Date"
                value={dateRange.endDate}
                onChange={(newValue) => {
                  setDateRange(prev => ({ ...prev, endDate: newValue }));
                }}
                renderInput={(params) => <TextField {...params} />}
                minDate={dateRange.startDate}
                maxDate={new Date()}
              />
              <Button 
                variant="contained" 
                onClick={fetchUsageData}
                disabled={loading}
              >
                Update
              </Button>
            </Box>
          </LocalizationProvider>
        </Paper>

        {loading ? (
          <Box display="flex" justifyContent="center" p={4}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Alert severity="error">{error}</Alert>
        ) : (
          <>
            <Grid container spacing={3} sx={{ mb: 4 }}>
              <Grid item xs={12} md={4}>
                <Card>
                  <CardContent>
                    <Typography color="textSecondary" gutterBottom>
                      Total Requests
                    </Typography>
                    <Typography variant="h4">
                      {usageData?.statistics?.totalRequests || 0}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} md={4}>
                <Card>
                  <CardContent>
                    <Typography color="textSecondary" gutterBottom>
                      Total Tokens Used
                    </Typography>
                    <Typography variant="h4">
                      {usageData?.statistics?.totalTokens?.toLocaleString() || 0}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} md={4}>
                <Card>
                  <CardContent>
                    <Typography color="textSecondary" gutterBottom>
                      Total Cost
                    </Typography>
                    <Typography variant="h4">
                      ${usageData?.statistics?.totalCost?.toFixed(2) || '0.00'}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>

            <Paper sx={{ p: 2 }}>
              <Typography variant="h6" gutterBottom>
                Recent Usage
              </Typography>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Date</TableCell>
                      <TableCell>Type</TableCell>
                      <TableCell>User</TableCell>
                      <TableCell>Tokens</TableCell>
                      <TableCell>Cost</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {usageData?.usage?.map((log) => (
                      <TableRow key={log.id}>
                        <TableCell>
                          {new Date(log.createdAt).toLocaleString()}
                        </TableCell>
                        <TableCell>{log.promptType}</TableCell>
                        <TableCell>{log.user?.name || 'Unknown'}</TableCell>
                        <TableCell>{log.tokens}</TableCell>
                        <TableCell>${log.cost.toFixed(4)}</TableCell>
                        <TableCell>
                          <Box
                            sx={{
                              color: log.status === 'success' ? 'success.main' : 'error.main',
                            }}
                          >
                            {log.status}
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Tooltip title="Preview Content">
                            <IconButton
                              size="small"
                              onClick={() => handlePreviewContent(log)}
                              disabled={!log.response || log.status !== 'success'}
                            >
                              <VisibilityIcon />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          </>
        )}
      </Box>

      {/* Preview Dialog */}
      <Dialog
        open={previewDialog.open}
        onClose={() => setPreviewDialog({ open: false, content: '', prompt: '' })}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Generated Content Preview</DialogTitle>
        <DialogContent dividers>
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" color="textSecondary" gutterBottom>
              Prompt:
            </Typography>
            <Paper 
              variant="outlined" 
              sx={{ 
                p: 2,
                backgroundColor: 'grey.50',
                whiteSpace: 'pre-wrap'
              }}
            >
              {previewDialog.prompt}
            </Paper>
          </Box>
          <Box 
            sx={{ 
              p: 2,
              '& img': { maxWidth: '100%', height: 'auto' },
              '& pre': {
                backgroundColor: 'grey.100',
                p: 2,
                borderRadius: 1,
                overflow: 'auto',
              },
            }}
          >
            <Typography variant="subtitle2" color="textSecondary" gutterBottom>
              Generated Content:
            </Typography>
            <div dangerouslySetInnerHTML={{ __html: previewDialog.content }} />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setPreviewDialog({ open: false, content: '', prompt: '' })}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </AccountLayout>
  );
}

export default AIUsageDashboard; 